/**
 * D11 — Opdracht-ledger (toets klaarzetten) tegen Supabase.
 *
 * Implementeert exact dezelfde signatures als de localStorage-variant in
 * `src/data/domains/teacherAssignments.ts`, zodat `USE_SUPABASE` een schakelaar
 * blijft en geen herschrijving van elke consumer.
 *
 * ─── WAAROM DIT DOMEIN ANDERS IS DAN D3 ─────────────────────────────────────
 *
 * `assessment_attempts` en `assessment_reports` hebben een `payload jsonb`: het
 * clientobject gaat er in zijn geheel in. Dit domein NIET. De opdracht is
 * volledig genormaliseerd over drie tabellen, omdat de RLS-policies erop moeten
 * kunnen redeneren:
 *
 *   assessment_assignments  de opdracht zelf (klas, domein, deadline, status)
 *   assignment_targets      wie hem moet maken   (1 rij per leerling)
 *   assignment_progress     wie hem af heeft     (1 rij per leerling per state)
 *
 * Een leerling mag een opdracht zien omdat er een rij in `assignment_targets`
 * staat — dat is met een array-kolom niet te doen. De prijs is dat één
 * `saveTeacherAssignment` hier meerdere statements is, en dat de vertaling
 * tussen clienttype en kolommen expliciet moet gebeuren.
 *
 * ─── DRIE HARDE GRENZEN, EN WAAROM ZE WEIGEREN IN PLAATS VAN AANPASSEN ──────
 *
 * Het clienttype staat dingen toe die het schema niet accepteert. Bij elk
 * daarvan weigeren we de schrijfactie met een duidelijke logregel, in plaats
 * van de waarde stilletjes bij te buigen:
 *
 * 1. `classroomId: null` — de kolom is `not null`. Een opdracht zonder klas
 *    heeft geen enkele leerkracht die hem via RLS mag zien; hij zou onzichtbaar
 *    landen.
 * 2. `status` buiten `active|archived` — het clienttype kent ook `expired` en
 *    `completed`, de check-constraint niet. Terugmappen naar `active` zou een
 *    verlopen opdracht weer openzetten.
 * 3. Een `id` die geen uuid is — de kolom is `uuid`. Zie `isUuid` hieronder.
 *
 * Stil bijbuigen levert een rij op die er gezond uitziet en het verkeerde
 * betekent. Dat is in dit project de duurste soort fout gebleken.
 */

import { supabase } from './client';
// `logger.ts` exporteert `error`; de codebase-conventie is de alias `logError`
// (zie o.a. `assessmentBackend.ts`).
import { error as logError } from '../../utils/logger';
import type { TeacherAssignment } from '../../assessment';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Opdracht-id's moeten uuid's zijn.
 *
 * De oude `genId('assignment')` maakte `assignment_l4k2j_x9f3qa` — prima voor
 * een localStorage-sleutel, maar `assessment_assignments.id` is een `uuid`.
 * Zo'n insert faalt met een typefout die niets zegt over de oorzaak. Sinds de
 * omzetting genereert `CreateAssessmentPanel` een echte uuid; deze controle
 * vangt opdrachten af die nog van vóór die wijziging in een browser staan.
 */
function isUuid(id: string): boolean {
  return UUID_RE.test(id);
}

/** Zet een timestamptz-string om naar de epoch-ms die de client verwacht. */
function toEpoch(value: unknown): number {
  if (typeof value !== 'string') return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}
type AssignmentRow = Record<string, unknown>;
function rowToAssignment(row: AssignmentRow, targets: string[], completed: string[], abandoned: string[]): TeacherAssignment {
  return {
    id: row.id as string,
    teacherId: row.created_by as string ?? '',
    classroomId: row.classroom_id as string ?? null,
    domainId: row.domain_id as TeacherAssignment['domainId'],
    domainLabel: row.domain_label as string ?? '',
    group: row.target_group as TeacherAssignment['group'],
    questionCount: row.question_count as number ?? 0,
    estimatedMinutes: row.estimated_minutes as number ?? 0,
    targetStudentIds: targets,
    wholeClass: Boolean(row.whole_class),
    selectedFamilyIds: row.selected_family_ids as string[] ?? [],
    type: row.assignment_type as TeacherAssignment['type'] ?? 'general',
    selectedSkillSummary: row.selected_skill_summary as string ?? '',
    createdAt: toEpoch(row.created_at),
    deadline: row.deadline ? toEpoch(row.deadline) : null,
    status: row.status as TeacherAssignment['status'],
    completedByStudentIds: completed,
    abandonedByStudentIds: abandoned
  };
}

/**
 * Alle opdrachten die ik mág zien.
 *
 * RLS bepaalt de scope: een leerkracht krijgt de opdrachten van zijn eigen
 * klassen, een leerling alleen die waar hij doelwit van is. "Alle opdrachten"
 * betekent hier dus iets anders dan in de localStorage-versie, waar iedereen
 * met dezelfde browser alles zag.
 */
export async function getTeacherAssignments(): Promise<Record<string, TeacherAssignment>> {
  const [rowsRes, targetsRes, progressRes] = await Promise.all([supabase.from('assessment_assignments').select('*'), supabase.from('assignment_targets').select('assignment_id, learner_id'), supabase.from('assignment_progress').select('assignment_id, learner_id, state')]);
  if (rowsRes.error) {
    logError('[Supabase] getTeacherAssignments failed', rowsRes.error);
    return {};
  }
  // Targets en progress zijn aanvullend: faalt een van beide, dan tonen we de
  // opdrachten liever zónder voortgang dan helemaal niet. Wel loggen — een
  // lege voortgangslijst is niet te onderscheiden van "niemand is begonnen",
  // en dat is precies het soort stilte dat we hier niet willen.
  if (targetsRes.error) {
    logError('[Supabase] getTeacherAssignments targets failed', targetsRes.error);
  }
  if (progressRes.error) {
    logError('[Supabase] getTeacherAssignments progress failed', progressRes.error);
  }
  const targetsBy = new Map<string, string[]>();
  for (const t of targetsRes.data ?? []) {
    const key = t.assignment_id as string;
    const list = targetsBy.get(key) ?? [];
    list.push(t.learner_id as string);
    targetsBy.set(key, list);
  }
  const completedBy = new Map<string, string[]>();
  const abandonedBy = new Map<string, string[]>();
  for (const p of progressRes.data ?? []) {
    const key = p.assignment_id as string;
    const bucket = p.state === 'completed' ? completedBy : abandonedBy;
    const list = bucket.get(key) ?? [];
    list.push(p.learner_id as string);
    bucket.set(key, list);
  }
  const out: Record<string, TeacherAssignment> = {};
  for (const row of rowsRes.data ?? []) {
    const id = (row as AssignmentRow).id as string;
    if (!id) continue;
    out[id] = rowToAssignment(row as AssignmentRow, targetsBy.get(id) ?? [], completedBy.get(id) ?? [], abandonedBy.get(id) ?? []);
  }
  return out;
}
export async function saveTeacherAssignment(id: string, assignment: TeacherAssignment): Promise<boolean> {
  if (!isUuid(id)) {
    logError(`[Supabase] saveTeacherAssignment geweigerd: id "${id}" is geen uuid. ` + 'Opdracht-ids moeten met crypto.randomUUID() gemaakt worden.');
    return false;
  }
  if (!assignment.classroomId) {
    logError('[Supabase] saveTeacherAssignment geweigerd: opdracht zonder classroomId. ' + 'Zonder klas kan geen enkele leerkracht hem via RLS zien.');
    return false;
  }
  if (assignment.status !== 'active' && assignment.status !== 'archived') {
    logError(`[Supabase] saveTeacherAssignment geweigerd: status "${assignment.status}" ` + 'wordt door het schema niet geaccepteerd (alleen active of archived).');
    return false;
  }
  const {
    error: upsertError
  } = await supabase.from('assessment_assignments').upsert({
    id,
    classroom_id: assignment.classroomId,
    created_by: assignment.teacherId,
    domain_id: assignment.domainId,
    domain_label: assignment.domainLabel,
    target_group: assignment.group,
    whole_class: assignment.wholeClass,
    question_count: assignment.questionCount,
    estimated_minutes: assignment.estimatedMinutes,
    selected_family_ids: assignment.selectedFamilyIds ?? [],
    assignment_type: assignment.type,
    selected_skill_summary: assignment.selectedSkillSummary,
    deadline: assignment.deadline ? new Date(assignment.deadline).toISOString() : null,
    status: assignment.status
  });
  if (upsertError) {
    logError('[Supabase] saveTeacherAssignment failed', upsertError);
    return false;
  }
  return syncTargets(id, assignment.targetStudentIds ?? []);
}

/**
 * Breng `assignment_targets` in lijn met de gewenste lijst.
 *
 * Eerst toevoegen, dan pas verwijderen. Andersom (leegmaken en opnieuw vullen)
 * is korter, maar laat bij een mislukte tweede stap een opdracht achter die
 * niemand meer kan maken. Nu is de slechtste uitkomst dat een leerling die
 * eraf moest hem nog even ziet staan — hersteld bij de volgende opslag.
 */
async function syncTargets(assignmentId: string, wanted: string[]): Promise<boolean> {
  const unique = Array.from(new Set(wanted));
  if (unique.length > 0) {
    const {
      error: insertError
    } = await supabase.from('assignment_targets').upsert(unique.map(learnerId => ({
      assignment_id: assignmentId,
      learner_id: learnerId
    })), {
      onConflict: 'assignment_id,learner_id',
      ignoreDuplicates: true
    });
    if (insertError) {
      logError('[Supabase] syncTargets insert failed', insertError);
      return false;
    }
  }
  let removal = supabase.from('assignment_targets').delete().eq('assignment_id', assignmentId);
  if (unique.length > 0) {
    removal = removal.not('learner_id', 'in', `(${unique.join(',')})`);
  }
  const {
    error: deleteError
  } = await removal;
  if (deleteError) {
    logError('[Supabase] syncTargets cleanup failed', deleteError);
    return false;
  }
  return true;
}

/**
 * Verwijder een opdracht.
 *
 * `assignment_targets` en `assignment_progress` hangen met `on delete cascade`
 * aan deze rij en verdwijnen mee. De toetsuitslagen zelf (`assessment_attempts`
 * / `assessment_reports`) NIET — die staan in een eigen domein met een eigen
 * bewaartermijn. De bevestigingstekst in het leerkrachtportaal zegt dat ook zo.
 */
export async function deleteTeacherAssignment(id: string): Promise<boolean> {
  const {
    error
  } = await supabase.from('assessment_assignments').delete().eq('id', id);
  if (error) {
    logError('[Supabase] deleteTeacherAssignment failed', error);
    return false;
  }
  return true;
}
export async function archiveTeacherAssignment(id: string): Promise<boolean> {
  // `select('id')` erbij zodat we een bestaande opdracht kunnen onderscheiden
  // van een die er niet (meer) is. Zonder die controle geeft een update op nul
  // rijen gewoon succes terug — hetzelfde patroon dat het adminscript eerder
  // stilletjes liet mislukken.
  const {
    data,
    error
  } = await supabase.from('assessment_assignments').update({
    status: 'archived'
  }).eq('id', id).select('id');
  if (error) {
    logError('[Supabase] archiveTeacherAssignment failed', error);
    return false;
  }
  if (!data || data.length === 0) {
    logError(`[Supabase] archiveTeacherAssignment: opdracht ${id} niet gevonden of niet zichtbaar.`);
    return false;
  }
  return true;
}

/**
 * Leg vast dat een leerling een opdracht heeft afgerond of afgebroken.
 *
 * Eén rij per (opdracht, leerling, state), dus de insert is vanzelf idempotent
 * met `ignoreDuplicates`. Dat komt overeen met het localStorage-contract:
 * tweemaal markeren geeft tweemaal `true`.
 *
 * `assignment_progress` heeft bewust alleen `select` en `insert` — geen update
 * of delete. Voortgang is een logboek: dat een kind een toets heeft afgebroken,
 * hoort niet weggepoetst te kunnen worden.
 *
 * De RLS-policy eist `learner_id = auth.uid()`, dus alleen het kind zelf schrijft
 * zijn eigen voortgang. Bestaat de opdracht niet, dan blokkeert de foreign key
 * de insert en komt hier `false` uit — precies zoals de lokale variant deed.
 */
async function markProgress(assignmentId: string, studentId: string, state: 'completed' | 'abandoned'): Promise<boolean> {
  const {
    error
  } = await supabase.from('assignment_progress').upsert({
    assignment_id: assignmentId,
    learner_id: studentId,
    state
  }, {
    onConflict: 'assignment_id,learner_id,state',
    ignoreDuplicates: true
  });
  if (error) {
    logError(`[Supabase] markProgress (${state}) failed`, error);
    return false;
  }
  return true;
}
export async function markAssignmentCompletedByStudent(assignmentId: string, studentId: string): Promise<boolean> {
  return markProgress(assignmentId, studentId, 'completed');
}
export async function markAssignmentAbandonedByStudent(assignmentId: string, studentId: string): Promise<boolean> {
  return markProgress(assignmentId, studentId, 'abandoned');
}